import React, { useCallback, useState, useEffect } from "react";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { BsExclamationTriangleFill } from "react-icons/bs";
import "./ExpiredLicenseBanner.scss";

const CountdownTimer = ({ duration, onExpire, key }) => {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [isActive, setIsActive] = useState(true);

  // Reset timer when duration changes (on route change or when shown again)
  useEffect(() => {
    setTimeLeft(duration);
    setIsActive(true);
  }, [duration, key]);

  useEffect(() => {
    if (!isActive || timeLeft <= 0) {
      if (timeLeft <= 0) {
        onExpire();
        setIsActive(false);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setIsActive(false);
          onExpire();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, isActive, onExpire]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const progressPercentage = (timeLeft / duration) * 100;
  const strokeDasharray = 2 * Math.PI * 20;
  const strokeDashoffset =
    strokeDasharray - (progressPercentage / 100) * strokeDasharray;

  return (
    <div className="countdown-timer">
      <div className="position-relative">
        <svg className="timer-svg" width="80" height="80" viewBox="0 0 50 50">
          <circle
            cx="25"
            cy="25"
            r="20"
            stroke="#f8d7da"
            strokeWidth="4"
            fill="none"
          />
          <circle
            cx="25"
            cy="25"
            r="20"
            stroke="#dc3545"
            strokeWidth="4"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="timer-circle-animated"
          />
        </svg>
        <div
          className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ top: 0, left: 0 }}
        >
          <span className="text-danger fw-bold" style={{ fontSize: "20px" }}>
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>
    </div>
  );
};

const ExpiredLicenseBanner = ({
  systemInfo,
  showLicenseModal,
  currentUser,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showModal, setShowModal] = useState(showLicenseModal);
  const [modalKey, setModalKey] = useState(0);

  useEffect(() => {
    const shouldShow =
      checkExpiryNotificationVisibility() &&
      systemInfo?.license?.isLicenseExpired;
    if (shouldShow) {
      setShowModal(true);
      setModalKey((prev) => prev + 1);
    } else {
      setShowModal(false);
    }
  }, [location.pathname, systemInfo, currentUser]);

  useEffect(() => {
    setShowModal(showLicenseModal);
  }, [showLicenseModal]);

  const handleCountdownComplete = useCallback(() => {
    setShowModal(false);
  }, []);

  const checkExpiryNotificationVisibility = () => {
    if (
      systemInfo?.license?.showNotification &&
      systemInfo?.license?.applicableRolesSlug &&
      currentUser
    ) {
      return systemInfo.license.applicableRolesSlug.includes(
        currentUser.role_slug
      );
    }
    return false;
  };

  return (
    <div
      className={`expired-license-modal modal fade ${
        showModal ? "show d-block" : ""
      }`}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content bg-white">
          {/* Header */}
          <div className="modal-header-custom">
            <BsExclamationTriangleFill
              className="header-icon text-white"
              size={24}
            />
            <h5 className="modal-title text-white">Warning</h5>
          </div>

          {/* Body */}
          <div className="modal-body-custom">
            <div className="d-flex flex-column align-items-center">
              {systemInfo?.license?.popupDuration > 0 ? (
                <div className="countdown-container mb-3">
                  <CountdownTimer
                    key={`timer-${modalKey}`}
                    duration={systemInfo?.license?.popupDuration}
                    onExpire={handleCountdownComplete}
                  />
                </div>
              ) : (
                <BsExclamationTriangleFill
                  className="warning-icon"
                  size={48}
                  color="#dc3545"
                />
              )}
              <h4 className="modal-title-custom mt-3 text-black">
                Your License has expired
              </h4>
              <p className="modal-message text-black">
                Please contact the administrator to renew or restore access.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpiredLicenseBanner;
